import React, { useState, useRef, useEffect } from 'react';

import CustomScrollbar from './CustomScrollbar';

interface TimePickerProps {
  label?: string;
  required?: boolean;
  value?: string;
  onChange: (time: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
  minTime?: string;
  maxTime?: string;
  step?: number; // in minutes
  selectedDate?: string; // Add selectedDate prop
}

const TimePicker: React.FC<TimePickerProps> = ({
  label,
  required = false,
  value = '',
  onChange,
  placeholder = 'Select time',
  disabled = false,
  error,
  helperText,
  className = '',
  minTime,
  maxTime,
  step = 15,
  selectedDate,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      setDropdownPosition(spaceBelow < 240 && spaceAbove > 240 ? 'top' : 'bottom');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const generateTimeOptions = () => {
    const times: string[] = [];
    const now = new Date();
    const today = new Date().toISOString().split('T')[0];
    const isToday = selectedDate === today;

    let startHour = 0;
    let startMinute = 0;

    // If it's today, start from current time + 15 minutes
    if (isToday) {
      const minTime = new Date(now.getTime() + 15 * 60 * 1000);
      startHour = minTime.getHours();
      startMinute = Math.ceil(minTime.getMinutes() / step) * step;

      if (startMinute >= 60) {
        startHour += 1;
        startMinute = 0;
      }
    }

    for (let hour = startHour; hour <= 23; hour++) {
      const minuteStart = hour === startHour ? startMinute : 0;
      for (let minute = minuteStart; minute < 60; minute += step) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute
          .toString()
          .padStart(2, '0')}`;

        // Check if time is within min/max range
        if (minTime && timeString < minTime) continue;
        if (maxTime && timeString > maxTime) continue;

        times.push(timeString);
      }
    }

    return times;
  };

  const formatDisplayTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleTimeSelect = (time: string) => {
    onChange(time);
    setIsOpen(false);
  };

  const timeOptions = generateTimeOptions();

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium mb-1 text-[#621C43]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input Field */}
      <div className="relative">
        <input
          type="time"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          className={`
            w-full px-3 py-[6px] pr-10
            border border-gray-300
            rounded-lg 
            bg-[#F2E9EA] 
            text-[#4A082D]
            focus:outline-none 
            focus:ring-2 
            focus:ring-[#621C43] 
            focus:ring-opacity-50
            disabled:opacity-50 
            disabled:cursor-not-allowed
            ${error ? 'border-red-500' : ''}
            [&::-webkit-calendar-picker-indicator]:opacity-0
            [&::-webkit-calendar-picker-indicator]:pointer-events-none
            ${value && !isFocused ? 'text-transparent' : ''}
          `}
        />
        {value && !isFocused && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#4A082D]">
            {formatDisplayTime(value)}
          </div>
        )}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-[#4A082D11] rounded transition-colors"
        >
          <svg
            className={`w-4 h-4 text-[#621C43] transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Time Dropdown */}
      {isOpen && !disabled && (
        <div
          className={`absolute ${
            dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
          } left-0 w-full bg-white border border-[#4A082D] rounded-lg shadow-lg z-50`}
        >
          <CustomScrollbar maxHeight="max-h-60">
            <div className="p-2">
              {timeOptions.map((time) => (
                <button
                  key={time}
                  type="button"
                  onClick={() => handleTimeSelect(time)}
                  className={`
                    w-full p-3 text-left rounded-lg transition-all duration-200
                    hover:bg-[#4A082D11]
                    ${value === time ? 'bg-[#621C43] text-white font-semibold' : 'text-[#4A082D]'}
                  `}
                >
                  <div className="flex justify-between items-center">
                    <span>{formatDisplayTime(time)}</span>
                    <span className="text-sm opacity-70">{time}</span>
                  </div>
                </button>
              ))}
            </div>
          </CustomScrollbar>
        </div>
      )}

      {/* Helper Text */}
      {helperText && !error && <p className="text-sm text-[#621C43] mt-1">{helperText}</p>}

      {/* Error Message */}
      {error && <p className="text-sm text-red-500 font-medium mt-1">{error}</p>}
    </div>
  );
};

export default TimePicker;
